import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Send, CheckCircle, XCircle, RefreshCw, FileText, Eye, Download, Wallet } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import ResultsAddModal from '../components/ResultsAddModal';
import { GOOSE_MARGIN_TIER_DEFAULTS } from './GooseOrderBuilder';

// The Open column per quarter as the kill deposit expects it: hour 0-23, MW, long positive.
const openPositions = (hours, baseNet) => hours.flatMap(hourData => hourData.quarters.map(q => ({
  hour: hourData.hour - 1,
  qh: q.qh,
  position: -Math.round(((q.final != null ? q.final : q.open) + baseNet) * 10) / 10,
})));

// Quarters bought on Fix I: the trading system keeps cash blocked for them until they settle.
const unsettledFixIBuys = (hours) => hours.flatMap(hourData => hourData.quarters
  .filter(q => q.fix_i.direction === 'Buy' && q.fix_i.volume > 0)
  .map(q => ({ label: `Fix I ${q.label}`, volume: q.fix_i.volume, price: q.fix_i.price })));

// Any Fix II fill on the schedule means the Fix II orders are already in.
const hasFixIIFills = (hours) => hours.some(hourData => hourData.quarters.some(q => q.fix_ii.volume > 0));

const fmtPln = (v) => Math.round(v).toLocaleString('pl-PL');

const KILL_DEPOSITS = [
  { key: 'soft_kill', label: 'Soft Kill Deposit' },
  { key: 'hard_kill', label: 'Hard Kill Deposit' },
];

const BisonSchedule = () => {
  const { fetchGet, fetchPost, axiosPost } = useApi();

  // Get tomorrow's date in Warsaw timezone (CET/CEST)
  const today = new Date();
  const warsawTomorrow = new Date(today.toLocaleString("en-US", {timeZone: "Europe/Warsaw"}));
  warsawTomorrow.setDate(warsawTomorrow.getDate() + 1);
  const tomorrowISO = `${warsawTomorrow.getFullYear()}-${String(warsawTomorrow.getMonth() + 1).padStart(2, '0')}-${String(warsawTomorrow.getDate()).padStart(2, '0')}`;

  const [formData, setFormData] = useState({
    date: tomorrowISO,
    file: null,
    ida1File: null
  });

  const [baseAction, setBaseAction] = useState('Idle');
  const [baseVolume, setBaseVolume] = useState(0);
  const [basePrice, setBasePrice] = useState(0);

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShowingSchedule, setIsShowingSchedule] = useState(false);
  const [result, setResult] = useState(null);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [isCalculatingResults, setIsCalculatingResults] = useState(false);
  const [calculatedMetrics, setCalculatedMetrics] = useState(null);
  const [calculationBreakdown, setCalculationBreakdown] = useState(null);
  const [calculationFxLookupDate, setCalculationFxLookupDate] = useState(null);
  const [calculationFxRate, setCalculationFxRate] = useState(null);
  const [showAddResultsModal, setShowAddResultsModal] = useState(false);
  const [addResultsLoading, setAddResultsLoading] = useState(false);
  const [addResultsError, setAddResultsError] = useState('');
  const [addResultsForm, setAddResultsForm] = useState({
    date: tomorrowISO,
    baseAction: 'Idle',
    basePrice: 0,
    volume: 0,
    isBaseActionInternal: false,
    bought: 0,
    sold: 0,
    totalVolume: 0,
    comment: ''
  });

  const transactionFeeRate = 0.05;
  const volumeFeeRate = 0.45;

  // Fetch base action from DB whenever date changes
  useEffect(() => {
    const fetchBaseAction = async () => {
      try {
        const response = await fetchGet(`/get-base-action?date=${formData.date}`);
        const data = await response.json();
        if (data.status === 'success') {
          setBaseAction(data.base_action || 'Idle');
          setBaseVolume(data.volume || 0);
          setBasePrice(0);
        } else {
          setBaseAction('Idle');
          setBaseVolume(0);
          setBasePrice(0);
        }
      } catch {
        setBaseAction('Idle');
        setBaseVolume(0);
        setBasePrice(0);
      }
    };
    fetchBaseAction();
  }, [fetchGet, formData.date]);

  // Soft / Hard Kill deposits: cash blocked with the GooseOrderBuilder Deterministic
  // Soft Kill / Hard Kill Fix II curves closing the Open position, plus the
  // unsettled Fix I buys. Only shown between the Fix I results and the Fix II
  // orders: hidden once the schedule has Fix II fills, and when the backend
  // answers not_applicable (no Fix I prices yet, or Fix II prices already out).
  // Recomputed when the schedule or the BASE inputs change (debounced for typing;
  // the last amount stays up meanwhile); stale answers dropped.
  const [killDeposit, setKillDeposit] = useState(null);
  const killDepositRequest = useRef(0);
  const killDepositFor = useRef(null);
  useEffect(() => {
    const hours = result?.summary?.hours;
    const request = ++killDepositRequest.current;
    if (killDepositFor.current !== result) {
      killDepositFor.current = result;
      setKillDeposit(null);
    }
    if (!hours) return undefined;
    if (hasFixIIFills(hours)) {
      setKillDeposit({ status: 'not_applicable' });
      return undefined;
    }
    const baseNet = baseAction === 'Buy' ? -baseVolume : baseAction === 'Sell' ? baseVolume : 0;
    const timer = setTimeout(() => {
      axiosPost('/bison-schedule-kill-deposit', {
        date: result.date,
        positions: openPositions(hours, baseNet),
        unsettled_buys: unsettledFixIBuys(hours),
        margin_tier1: GOOSE_MARGIN_TIER_DEFAULTS.marginTier1,
        margin_tier2: GOOSE_MARGIN_TIER_DEFAULTS.marginTier2,
        margin_tier3: GOOSE_MARGIN_TIER_DEFAULTS.marginTier3,
        margin_tier4: GOOSE_MARGIN_TIER_DEFAULTS.marginTier4,
      })
        .then(response => {
          if (request === killDepositRequest.current) setKillDeposit(response.data);
        })
        .catch(err => {
          if (request === killDepositRequest.current) {
            setKillDeposit({ status: 'error', message: err.message || 'Failed to compute kill deposit' });
          }
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [result, baseAction, baseVolume, axiosPost]);

  const getBaseNet = (action, volume) => (
    action === 'Buy' ? -volume : action === 'Sell' ? volume : 0
  );

  const hasOpenPositionsForBase = (hours, action, volume) => {
    const baseNet = getBaseNet(action, volume);
    return hours.some(hourData =>
      hourData.quarters.some(q =>
        Math.round((((q.final != null ? q.final : q.open) + baseNet) || 0) * 10) / 10 !== 0
      )
    );
  };

  const computeSavedMetricsFromForm = (currentForm) => {
    const pnl = (currentForm.sold || 0) - (currentForm.bought || 0);
    const baseVolumeForFee = currentForm.isBaseActionInternal && currentForm.baseAction !== 'Idle'
      ? Math.abs(currentForm.volume || 0) * 24
      : 0;
    const volumeForFee = Math.max(0, (currentForm.totalVolume || 0) - baseVolumeForFee);
    const volumeFee = volumeFeeRate * volumeForFee;
    const transactionFee = (currentForm.sold || 0) * (transactionFeeRate / 100);
    const pnlMinusFee = pnl - volumeFee - transactionFee;
    const pnlPerMwh = (currentForm.totalVolume || 0) > 0
      ? pnlMinusFee / currentForm.totalVolume
      : 0;

    return computeSummaryInfo({
      pnl,
      pnl_minus_fee: pnlMinusFee,
      pnl_per_mwh: pnlPerMwh,
      total_volume: currentForm.totalVolume || 0,
    });
  };

  const handleSubmit = async () => {
    if (!formData.file && !formData.ida1File) {
      setStatus({ type: 'error', message: 'Please upload at least one file (BOŚ Transactions or IDA1 Splitter)' });
      return;
    }
    if (!formData.date) {
      setStatus({ type: 'error', message: 'Please select a date' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: 'info', message: 'Generating bison schedule...' });
    setResult(null);
    setCalculatedMetrics(null);
    setCalculationBreakdown(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('date', formData.date);
      formDataToSend.append('base_action', baseAction);
      formDataToSend.append('base_volume', baseVolume);
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }
      if (formData.ida1File) {
        formDataToSend.append('ida1_file', formData.ida1File);
      }

      const response = await fetchPost('/generate-bison-schedule', formDataToSend);
      const data = await response.json();

      if (data.status === 'success') {
        setResult(data);
        setStatus({ type: 'success', message: 'Bison schedule generated successfully!' });
        setFormData(prev => ({ ...prev, file: null, ida1File: null }));
      } else {
        setStatus({ type: 'error', message: data.message || 'Generation failed' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error generating bison schedule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowSchedule = async () => {
    if (!formData.date) {
      setStatus({ type: 'error', message: 'Please select a date' });
      return;
    }

    setIsShowingSchedule(true);
    setStatus({ type: 'info', message: 'Loading bison schedule...' });
    setResult(null);
    setCalculatedMetrics(null);
    setCalculationBreakdown(null);

    try {
      const response = await axiosPost('/show-bison-schedule', { date: formData.date, base_action: baseAction, base_volume: baseVolume });

      if (response.data.status === 'success') {
        setResult(response.data);
        setStatus(null);
      } else {
        setStatus({ type: 'error', message: response.data.message || 'Failed to load schedule' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Network error. Please check your connection.' });
      console.error('Error showing bison schedule:', error);
    } finally {
      setIsShowingSchedule(false);
    }
  };

  const formatVolume = (value) => {
    if (!value || value === 0) return '-';
    const rounded = Math.round(value * 10) / 10;
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
  };

  const formatPosition = (value) => {
    if (value === 0) return '0';
    const rounded = Math.round(value * 10) / 10;
    const formatted = rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
    if (rounded > 0) return `+${formatted}`;
    return formatted;
  };

  const getPositionClass = (value) => {
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadExcel = async () => {
    if (!result?.date) return;
    setIsDownloading(true);
    try {
      // Re-generate excel with current base_action/volume
      const response = await axiosPost('/show-bison-schedule', {
        date: result.date,
        base_action: baseAction,
        base_volume: baseVolume,
      });
      const data = response.data;
      if (data.status === 'success' && data.excel_content) {
        const byteCharacters = atob(data.excel_content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.output_filename || `bison_grafik_dostaw_${result.date}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        setStatus({ type: 'error', message: data.message || 'Failed to generate Excel' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Failed to download schedule' });
    } finally {
      setIsDownloading(false);
    }
  };

  const getDirectionClass = (direction) => {
    if (direction === 'Buy') return 'text-green-600';
    if (direction === 'Sell') return 'text-red-600';
    return 'text-gray-400';
  };

  const handleAddFormChange = (field, value) => {
    setAddResultsForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'baseAction' && value === 'Idle') {
        updated.basePrice = 0;
        updated.volume = 0;
        updated.isBaseActionInternal = false;
      }
      return updated;
    });
  };

  const closeAddResultsModal = () => {
    setShowAddResultsModal(false);
    setAddResultsError('');
  };

  const formatPln = (value) => (value || 0).toFixed(2);

  const computeSummaryInfo = (metrics) => {
    if (!metrics) return null;
    return {
      pnl: metrics.pnl ?? 0,
      pnlMinusFee: metrics.pnl_minus_fee ?? 0,
      pnlPerMwh: metrics.pnl_per_mwh ?? 0,
      totalVolume: metrics.total_volume ?? 0,
    };
  };

  const handleConfirmCalculateResults = async () => {
    if (result?.summary?.hours && hasOpenPositionsForBase(result.summary.hours, addResultsForm.baseAction, addResultsForm.volume)) {
      setAddResultsError('Open positions must be 0 for the selected Base Action and Base Volume before calculating results.');
      return;
    }

    setIsCalculatingResults(true);
    setAddResultsError('');

    try {
      const response = await axiosPost('/calculate-bison-schedule-results', {
        date: result?.date || formData.date,
        base_action: addResultsForm.baseAction,
        base_volume: addResultsForm.volume,
        base_price: addResultsForm.basePrice,
        transaction_fee_rate: transactionFeeRate,
        volume_fee_rate: volumeFeeRate,
        comment: addResultsForm.comment || ''
      });

      if (response.data.status === 'success') {
        const prefill = response.data.prefill || {};
        setAddResultsForm({
          date: prefill.date || formData.date,
          baseAction: prefill.base_action || addResultsForm.baseAction,
          basePrice: prefill.base_price || 0,
          volume: prefill.base_volume || 0,
          isBaseActionInternal: Boolean(prefill.is_base_action_internal),
          bought: prefill.bought || 0,
          sold: prefill.sold || 0,
          totalVolume: prefill.total_volume || 0,
          comment: prefill.comment || ''
        });
        setCalculationBreakdown(response.data.breakdown || null);
        setCalculationFxLookupDate(response.data.fx_lookup_date || null);
        setCalculationFxRate(response.data.eur_pln_rate ?? null);
        setShowCalcModal(false);
        setShowBreakdownModal(true);
      } else {
        setStatus({ type: 'error', message: response.data.message || 'Failed to calculate results' });
      }
    } catch (error) {
      console.error('Error calculating bison schedule results:', error);
      setStatus({ type: 'error', message: 'Failed to calculate results' });
    } finally {
      setIsCalculatingResults(false);
    }
  };

  const handleSubmitCalculatedResult = async () => {
    setAddResultsLoading(true);
    setAddResultsError('');
    try {
      const response = await axiosPost('/add-result', {
        date: addResultsForm.date,
        base_action: addResultsForm.baseAction,
        base_price: addResultsForm.baseAction !== 'Idle' ? addResultsForm.basePrice : 0,
        volume: addResultsForm.baseAction !== 'Idle' ? addResultsForm.volume : 0,
        is_base_action_internal: addResultsForm.baseAction !== 'Idle' ? addResultsForm.isBaseActionInternal : false,
        bought: addResultsForm.bought,
        sold: addResultsForm.sold,
        total_volume: addResultsForm.totalVolume,
        comment: addResultsForm.comment,
        volume_fee_rate: volumeFeeRate,
        transaction_fee_rate: transactionFeeRate
      });

      if (response.data.status === 'success') {
        setCalculatedMetrics(computeSavedMetricsFromForm(addResultsForm));
        setStatus({ type: 'success', message: response.data.message || 'Result added successfully' });
        closeAddResultsModal();
      } else {
        setAddResultsError(response.data.message || 'Failed to add result');
      }
    } catch (error) {
      console.error('Error adding result from BisonSchedule:', error);
      setAddResultsError(error.message || 'Failed to add result');
    } finally {
      setAddResultsLoading(false);
    }
  };

  const handleConfirmBreakdown = () => {
    setShowBreakdownModal(false);
    setShowAddResultsModal(true);
  };

  const handleBackToCalculation = () => {
    setShowBreakdownModal(false);
    setShowCalcModal(true);
  };

  // Check if any quarter has IDA1 data
  const hasIda1Data = (hours) => {
    return hours.some(h => h.quarters.some(q => q.ida1 && q.ida1.volume > 0));
  };

  // Compute totals for footer
  const computeTotals = (hours, bNet) => {
    let fixIBuy = 0, fixISell = 0;
    let fixIIBuy = 0, fixIISell = 0;
    let openBuy = 0, openSell = 0;
    let ida1Buy = 0, ida1Sell = 0;
    let finalBuy = 0, finalSell = 0;

    for (const h of hours) {
      for (const q of h.quarters) {
        if (q.fix_i.direction === 'Buy') fixIBuy += q.fix_i.volume;
        if (q.fix_i.direction === 'Sell') fixISell += q.fix_i.volume;
        if (q.fix_ii.direction === 'Buy') fixIIBuy += q.fix_ii.volume;
        if (q.fix_ii.direction === 'Sell') fixIISell += q.fix_ii.volume;
        const adjOpen = q.open + bNet;
        if (adjOpen > 0) openSell += adjOpen;
        if (adjOpen < 0) openBuy += Math.abs(adjOpen);
        if (q.ida1 && q.ida1.direction === 'Buy') ida1Buy += q.ida1.volume;
        if (q.ida1 && q.ida1.direction === 'Sell') ida1Sell += q.ida1.volume;
        const adjFinal = (q.final != null ? q.final : q.open) + bNet;
        if (adjFinal > 0) finalSell += adjFinal;
        if (adjFinal < 0) finalBuy += Math.abs(adjFinal);
      }
    }

    const fmt = (v) => {
      const r = Math.round(v * 10) / 10;
      return r % 1 === 0 ? r.toString() : r.toFixed(1);
    };

    return {
      fixIBuy: fmt(fixIBuy), fixISell: fmt(fixISell),
      fixIIBuy: fmt(fixIIBuy), fixIISell: fmt(fixIISell),
      openBuy: fmt(openBuy), openSell: fmt(openSell),
      ida1Buy: fmt(ida1Buy), ida1Sell: fmt(ida1Sell),
      finalBuy: fmt(finalBuy), finalSell: fmt(finalSell)
    };
  };

  return (
    <div className="space-y-8">
      {/* Date Selection */}
      <div className="bg-gray-50 border border-gray-200 p-6 rounded-xl">
        <label className="flex items-center space-x-3 text-lg font-semibold text-gray-700 mb-3">
          <Calendar className="w-6 h-6" />
          <span>Date of Transactions</span>
          <span className="text-red-500">*</span>
        </label>
        <input
          type="date"
          value={formData.date}
          onChange={(e) => {
            const newDate = e.target.value;
            if (newDate) setFormData(prev => ({ ...prev, date: newDate }));
          }}
          className={`w-full px-4 py-3 border-2 ${formData.date ? 'border-green-400' : 'border-gray-300'} rounded-lg focus:border-gray-500 focus:outline-none transition-all font-mono text-lg`}
        />
      </div>

      {/* File Uploads */}
      <div className="border-2 border-gray-200 rounded-xl p-6 hover:border-gray-400 transition-all">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <div className="w-1 h-8 bg-gray-600 mr-3"></div>
          Transactions
          <span className="ml-auto text-sm font-normal text-gray-600">
            {(formData.file ? 1 : 0) + (formData.ida1File ? 1 : 0)}/2 files uploaded
          </span>
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {/* BOŚ Transactions (Historia transakcji) */}
          <div className={`bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-all ${formData.file ? '' : 'border-2 border-dashed border-gray-300'}`}>
            <label className="block cursor-pointer">
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">BOŚ Transactions</span>
              </div>
              <input
                type="file"
                accept=".csv,.xlsx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all"
                onChange={(e) => setFormData(prev => ({ ...prev, file: e.target.files[0] }))}
              />
              {formData.file && (
                <p className="mt-2 text-xs text-green-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formData.file.name}
                </p>
              )}
            </label>
          </div>

          {/* Nord Pool IDA1 Splitter */}
          <div className={`bg-gray-50 p-4 rounded-lg hover:bg-gray-100 transition-all ${formData.ida1File ? '' : 'border-2 border-dashed border-gray-300'}`}>
            <label className="block cursor-pointer">
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-gray-700">Nord Pool IDA1 Splitter</span>
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-all"
                onChange={(e) => setFormData(prev => ({ ...prev, ida1File: e.target.files[0] }))}
              />
              {formData.ida1File && (
                <p className="mt-2 text-xs text-green-600 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {formData.ida1File.name}
                </p>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          onClick={handleShowSchedule}
          disabled={isShowingSchedule || isSubmitting || !formData.date}
          className="flex-1 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          <Eye className="w-5 h-5" />
          {isShowingSchedule ? 'Loading...' : 'Show Schedule'}
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || isShowingSchedule || (!formData.file && !formData.ida1File) || !formData.date}
          className="flex-1 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
        >
          <Send className="w-5 h-5" />
          {isSubmitting ? 'Generating...' : 'Generate Schedule'}
        </button>
      </div>

      {/* Status message */}
      {status && status.message && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          status.type === 'success' ? 'bg-green-100 text-green-800' :
          status.type === 'error' ? 'bg-red-100 text-red-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {status.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {status.type === 'error' && <XCircle className="w-5 h-5" />}
          {status.type === 'info' && <RefreshCw className="w-5 h-5 animate-spin" />}
          <p className="font-medium">{status.message}</p>
        </div>
      )}

      {/* Results — Quarterly Position Table */}
      {result && result.summary && (() => {
        const showIda1 = hasIda1Data(result.summary.hours);
        const baseNet = getBaseNet(baseAction, baseVolume);
        const hasOpenPositions = hasOpenPositionsForBase(result.summary.hours, baseAction, baseVolume);

        // (highlighting logic moved inline — yellow when Open position ≠ 0)

        return (
        <div className="border-2 border-gray-200 rounded-xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center whitespace-nowrap">
                <div className="w-1 h-8 bg-teal-600 mr-3"></div>
                Position Summary for {result.date}
              </h2>
              {/* Only between the Fix I results and the Fix II orders (see the effect above). */}
              {killDeposit && killDeposit.status !== 'not_applicable' && (
                <div className="flex flex-col gap-0.5">
                  {KILL_DEPOSITS.map(({ key, label }) => {
                    const d = killDeposit.status === 'success' ? killDeposit[key] : null;
                    const unsettled = d ? killDeposit.unsettled_buys : null;
                    return (
                      <div
                        key={key}
                        className="flex items-baseline gap-1.5 whitespace-nowrap"
                        title={d
                          ? `Closing: Deterministic ${label.replace(' Deposit', '')} curves for the Open position, worst case ${fmtPln(d.closing.exposure)} + fees ${d.closing.fees.toFixed(2)} PLN (${d.closing.buy_volume_mwh} MWh bought at most)\n`
                            + `Unsettled Fix I buys: ${unsettled.buy_volume_mwh} MWh, worth ${unsettled.value.toFixed(2)} + fees ${unsettled.fees.toFixed(2)} PLN`
                          : killDeposit.message}
                      >
                        <Wallet className="w-4 h-4 text-gray-400 self-center" />
                        <span className="w-36 text-xs font-semibold uppercase text-gray-500">{label}</span>
                        {d ? (
                          <>
                            <span className="text-lg font-bold text-gray-800">
                              {fmtPln(d.deposit)} <span className="text-xs font-normal text-gray-500">PLN</span>
                            </span>
                            <span className="text-xs text-gray-500">
                              = closing {fmtPln(d.closing.deposit)} + unsettled Fix I buys {fmtPln(unsettled.deposit)}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-red-600">error</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {result.excel_content && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <label className="text-xs font-medium text-gray-500">Base Action</label>
                  <select
                    value={baseAction}
                    onChange={(e) => setBaseAction(e.target.value)}
                    className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="Idle">Idle</option>
                    <option value="Buy">Buy</option>
                    <option value="Sell">Sell</option>
                  </select>
                </div>
                <div className="flex items-center space-x-1">
                  <label className="text-xs font-medium text-gray-500">Vol (MW)</label>
                  <input
                    type="number"
                    value={baseVolume}
                    onChange={(e) => setBaseVolume(parseFloat(e.target.value) || 0)}
                    className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                    step="0.1"
                    min="0"
                  />
                </div>
                <button
                  onClick={downloadExcel}
                  disabled={isDownloading}
                  className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
                >
                  {isDownloading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                  <span>{isDownloading ? 'Generating...' : 'Download Schedule'}</span>
                </button>
                <button
                  onClick={() => {
                    setAddResultsForm(prev => ({
                      ...prev,
                      date: result.date || formData.date,
                      baseAction,
                      volume: baseVolume,
                      basePrice,
                      isBaseActionInternal: false,
                      comment: '',
                    }));
                    setAddResultsError('');
                    setCalculationBreakdown(null);
                    setCalculationFxLookupDate(null);
                    setCalculationFxRate(null);
                    setShowCalcModal(true);
                  }}
                  disabled={hasOpenPositions || isCalculatingResults}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-all"
                >
                  {isCalculatingResults ? <RefreshCw className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  <span>{isCalculatingResults ? 'Calculating...' : 'Calculate Results'}</span>
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b-2 border-gray-300" rowSpan="2">Quarter</th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-amber-50" colSpan="2">Base</th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-blue-50" colSpan="3">Fixing I (RDN)</th>
                  <th className="px-2 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-green-50" colSpan="3">Fixing II (DAM)</th>
                  {showIda1 && (
                    <th className="px-2 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-indigo-50" colSpan="3">IDA1 (Nord Pool)</th>
                  )}
                  <th className="px-2 py-2 text-center font-semibold text-gray-700 border-b border-gray-300 bg-purple-50" colSpan="5">Position</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-amber-50">Vol</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-amber-50">Action</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Vol</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Price</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-blue-50">Action</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Vol</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Price</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-green-50">Action</th>
                  {showIda1 && (
                    <>
                      <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-indigo-50">Vol</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-indigo-50">Price</th>
                      <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-indigo-50">Action</th>
                    </>
                  )}
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Base</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Fix I</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Fix II</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">IDA1</th>
                  <th className="px-2 py-2 text-center font-medium text-gray-600 border-b border-gray-200 bg-purple-50">Open</th>
                </tr>
              </thead>
              <tbody>
                {result.summary.hours.map((hourData, hIdx) => (
                  hourData.quarters.map((q, qIdx) => {
                    const isFirstQ = qIdx === 0;
                    const openVal = Math.round(((q.final != null ? q.final : q.open) + baseNet) * 10) / 10;
                    const isOutlier = openVal !== 0;
                    const hourBg = isOutlier ? 'bg-yellow-200' : (hIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50');
                    return (
                      <tr key={q.label} className={hourBg}>
                        {/* Quarter label */}
                        <td className={`px-2 py-1 font-mono text-xs text-gray-700 border-b border-gray-100 ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                          {isFirstQ && <span className="font-semibold text-gray-800 mr-1">H{String(hourData.hour).padStart(2, '0')}</span>}
                          <span className="text-gray-500">{q.time_label}</span>
                        </td>

                        {/* Base — show only on first quarter row, span 4 */}
                        {isFirstQ ? (
                          <>
                            <td className="px-2 py-1 text-center border-b border-gray-100 bg-amber-50/30 border-t border-gray-200" rowSpan="4">
                              {baseAction !== 'Idle' ? formatVolume(baseVolume) : '-'}
                            </td>
                            <td className={`px-2 py-1 text-center border-b border-gray-100 bg-amber-50/30 font-medium border-t border-gray-200 ${getDirectionClass(baseAction !== 'Idle' ? baseAction : null)}`} rowSpan="4">
                              {baseAction !== 'Idle' ? baseAction : '-'}
                            </td>
                          </>
                        ) : null}

                        {/* Fix I per quarter */}
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-blue-50/30 ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                          {formatVolume(q.fix_i.volume)}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-blue-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                          {q.fix_i.price != null ? q.fix_i.price.toFixed(2) : '-'}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-blue-50/30 font-medium ${isFirstQ ? 'border-t border-gray-200' : ''} ${getDirectionClass(q.fix_i.direction)}`}>
                          {q.fix_i.direction || '-'}
                        </td>

                        {/* Fix II per quarter */}
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-green-50/30 ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                          {formatVolume(q.fix_ii.volume)}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-green-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                          {q.fix_ii.price != null ? q.fix_ii.price.toFixed(2) : '-'}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-green-50/30 font-medium ${isFirstQ ? 'border-t border-gray-200' : ''} ${getDirectionClass(q.fix_ii.direction)}`}>
                          {q.fix_ii.direction || '-'}
                        </td>

                        {/* IDA1 per quarter */}
                        {showIda1 && (
                          <>
                            <td className={`px-2 py-1 text-center border-b border-gray-100 bg-indigo-50/30 ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                              {formatVolume(q.ida1?.volume)}
                            </td>
                            <td className={`px-2 py-1 text-center border-b border-gray-100 bg-indigo-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''}`}>
                              {q.ida1?.price != null ? q.ida1.price.toFixed(2) : '-'}
                            </td>
                            <td className={`px-2 py-1 text-center border-b border-gray-100 bg-indigo-50/30 font-medium ${isFirstQ ? 'border-t border-gray-200' : ''} ${getDirectionClass(q.ida1?.direction)}`}>
                              {q.ida1?.direction || '-'}
                            </td>
                          </>
                        )}

                        {/* Positions (sign-reverted: long=+, short=-) */}
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''} ${getPositionClass(-baseNet)}`}>
                          {formatPosition(-baseNet)}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''} ${getPositionClass(-q.fix_i_net)}`}>
                          {formatPosition(-q.fix_i_net)}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''} ${getPositionClass(-q.fix_ii_net)}`}>
                          {formatPosition(-q.fix_ii_net)}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-purple-50/30 font-mono ${isFirstQ ? 'border-t border-gray-200' : ''} ${getPositionClass(-(q.ida1_net || 0))}`}>
                          {formatPosition(-(q.ida1_net || 0))}
                        </td>
                        <td className={`px-2 py-1 text-center border-b border-gray-100 bg-purple-50/30 font-mono font-bold ${isFirstQ ? 'border-t border-gray-200' : ''} ${getPositionClass(-((q.final != null ? q.final : q.open) + baseNet))}`}>
                            {formatPosition(-((q.final != null ? q.final : q.open) + baseNet))}
                          </td>
                      </tr>
                    );
                  })
                ))}
              </tbody>
              <tfoot>
                {(() => {
                  const t = computeTotals(result.summary.hours, baseNet);
                  const baseTotalBuy = baseAction === 'Buy' ? Math.round(baseVolume * 24 * 10) / 10 : 0;
                  const baseTotalSell = baseAction === 'Sell' ? Math.round(baseVolume * 24 * 10) / 10 : 0;
                  const fmtB = (v) => { const r = Math.round(v * 10) / 10; return r % 1 === 0 ? r.toString() : r.toFixed(1); };
                  return (
                    <tr className="bg-gray-100 font-semibold">
                      <td className="px-2 py-2 text-gray-700 border-t-2 border-gray-300">Total</td>
                      <td className="px-2 py-2 text-center border-t-2 border-gray-300 bg-amber-50" colSpan="2">
                        {baseAction !== 'Idle' ? (
                          <>
                            <span className={baseAction === 'Buy' ? 'text-green-600' : 'text-red-600'}>
                              {baseAction}: {fmtB(baseAction === 'Buy' ? baseTotalBuy : baseTotalSell)}
                            </span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-2 text-center border-t-2 border-gray-300 bg-blue-50" colSpan="3">
                        <span className="text-green-600">Buy: {t.fixIBuy}</span>
                        {' / '}
                        <span className="text-red-600">Sell: {t.fixISell}</span>
                      </td>
                      <td className="px-2 py-2 text-center border-t-2 border-gray-300 bg-green-50" colSpan="3">
                        <span className="text-green-600">Buy: {t.fixIIBuy}</span>
                        {' / '}
                        <span className="text-red-600">Sell: {t.fixIISell}</span>
                      </td>
                      {showIda1 && (
                        <td className="px-2 py-2 text-center border-t-2 border-gray-300 bg-indigo-50" colSpan="3">
                          <span className="text-green-600">Buy: {t.ida1Buy}</span>
                          {' / '}
                          <span className="text-red-600">Sell: {t.ida1Sell}</span>
                        </td>
                      )}
                      <td className="px-2 py-2 text-center border-t-2 border-gray-300 bg-purple-50 font-mono" colSpan="5">
                        {showIda1 ? (
                          <>
                            <span className="text-green-600">Long: {t.finalBuy}</span>
                            {' / '}
                            <span className="text-red-600">Short: {t.finalSell}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-green-600">Long: {t.openBuy}</span>
                            {' / '}
                            <span className="text-red-600">Short: {t.openSell}</span>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </div>
        );
      })()}

      {calculatedMetrics && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-4">
          <div className="text-sm font-semibold text-emerald-800 mb-3">Calculated Result Summary</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <div className="text-gray-500">PnL</div>
              <div className={`font-semibold ${calculatedMetrics.pnl >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{calculatedMetrics.pnl.toFixed(2)} PLN</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <div className="text-gray-500">PnL after fees</div>
              <div className={`font-semibold ${calculatedMetrics.pnlMinusFee >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{calculatedMetrics.pnlMinusFee.toFixed(2)} PLN</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <div className="text-gray-500">PLN/MWh</div>
              <div className="font-semibold text-gray-800">{calculatedMetrics.pnlPerMwh.toFixed(2)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-emerald-100">
              <div className="text-gray-500">Total Volume</div>
              <div className="font-semibold text-gray-800">{calculatedMetrics.totalVolume.toFixed(2)} MWh</div>
            </div>
          </div>
        </div>
      )}

      {showCalcModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Calculate Results</h2>
              <button onClick={() => {
                setShowCalcModal(false);
                setAddResultsError('');
              }} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {addResultsError && (
                <div className="bg-red-50 rounded-lg p-3 text-sm text-red-700">
                  {addResultsError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={addResultsForm.date}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-100 text-gray-600 rounded-lg cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base Action</label>
                <select
                  value={addResultsForm.baseAction}
                  onChange={(e) => handleAddFormChange('baseAction', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="Idle">Idle</option>
                  <option value="Buy">Buy</option>
                  <option value="Sell">Sell</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base Volume (MW)</label>
                <input
                  type="number"
                  step="0.01"
                  value={addResultsForm.volume}
                  onChange={(e) => handleAddFormChange('volume', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Base Price (PLN)</label>
                <input
                  type="number"
                  step="0.01"
                  value={addResultsForm.basePrice}
                  onChange={(e) => handleAddFormChange('basePrice', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowCalcModal(false);
                  setAddResultsError('');
                }}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCalculateResults}
                disabled={isCalculatingResults}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isCalculatingResults ? 'Calculating...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBreakdownModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Buy And Sell Breakdown</h2>
              <button
                onClick={handleBackToCalculation}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <XCircle className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="text-gray-500">Date</div>
                  <div className="font-semibold text-gray-800">{addResultsForm.date}</div>
                </div>
                {calculationFxRate != null && (
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="text-gray-500">IDA1 FX</div>
                    <div className="font-semibold text-gray-800">
                      {calculationFxRate.toFixed(4)} PLN/EUR
                      {calculationFxLookupDate ? ` from ${calculationFxLookupDate}` : ''}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <div className="text-sm text-emerald-700">Proposed Result</div>
                <div className="text-lg font-semibold text-emerald-900">
                  {formatPln(addResultsForm.sold)} - {formatPln(addResultsForm.bought)} = {formatPln((addResultsForm.sold || 0) - (addResultsForm.bought || 0))} PLN
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Auction</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Sold (PLN)</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Bought (PLN)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'base', label: 'BASE' },
                      { key: 'fixing_i', label: 'Fixing I' },
                      { key: 'fixing_ii', label: 'Fixing II' },
                      { key: 'ida1', label: 'IDA1' },
                    ].map((row) => (
                      <tr key={row.key} className="border-t border-gray-100">
                        <td className="px-4 py-3 font-medium text-gray-800">{row.label}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">
                          {formatPln(calculationBreakdown?.[row.key]?.sold)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">
                          {formatPln(calculationBreakdown?.[row.key]?.bought)}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-gray-200 bg-emerald-50">
                      <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                        {formatPln(addResultsForm.sold)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-gray-900">
                        {formatPln(addResultsForm.bought)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleBackToCalculation}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirmBreakdown}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <ResultsAddModal
        show={showAddResultsModal}
        formData={addResultsForm}
        error={addResultsError}
        loading={addResultsLoading}
        onClose={closeAddResultsModal}
        onChange={handleAddFormChange}
        onSubmit={handleSubmitCalculatedResult}
      />
    </div>
  );
};

export default BisonSchedule;
